import { useState } from 'react';
import { OrgUnitModel } from '../../../../types';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { UserKeys, editOrgUnit } from '../../../api/users-api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { handleErrors } from '../../../utils/errors';
import { useStore } from '../../state/zustand-store';
import { Controller, useForm } from 'react-hook-form';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import Autocomplete from '@mui/material/Autocomplete';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
interface EditOrgUnitDialogProps {
  open: boolean;
  handleClose: () => void;
  changeLogRoles: string[];
  orgUnits: OrgUnitModel[];
  selectedOrgUnitId: string;
}

export default function EditOrgUnitDialog({
  open,
  handleClose,
  changeLogRoles,
  orgUnits,
  selectedOrgUnitId,
}: EditOrgUnitDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: (data: Partial<OrgUnitModel>) => {
      // Ths is used for bigquery indexing purposes for org units with children
      const hasChildren = data.children ? data.children.length > 0 : false;
      const isRenameOrMoveWithChildren =
        (data.orgUnitPath !== selectOrg?.orgUnitPath && hasChildren) ||
        (data.name !== selectOrg?.name && hasChildren);
      delete data.children;

      return editOrgUnit(
        {
          ...data,
          parentOrgUnitId: orgUnits.find(
            (org) => org.orgUnitPath === data.parentOrgUnitPath
          )?.orgUnitId,
        },
        changeLogMessage,
        isRenameOrMoveWithChildren
      );
    },
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Organizational unit edited successfully!',
      });

      queryClient.invalidateQueries({ queryKey: [UserKeys.OrgUnitModels] });
      queryClient.invalidateQueries({
        queryKey: [UserKeys.OrgUnitsFromGoogle],
      });
      queryClient.invalidateQueries({ queryKey: [UserKeys.User] });
      queryClient.invalidateQueries({ queryKey: [UserKeys.UserList] });

      reset();
      handleClose();
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue editing the organizational unit. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.organizational_units.update_org_unit, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.organizationalUnits,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const selectOrg = orgUnits.find(
    (org) => org.orgUnitPath === selectedOrgUnitId
  );

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...selectOrg,
    },
  });

  function onSubmit(data: Partial<OrgUnitModel>) {
    if (
      isAbleToSeeUIElement(changeLogRoles, DirectoryRoles.EditOrgUnit.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        reset();
        handleClose();
      }}
      title={`Edit Organizational Unit`}
      dialogContent={
        <Stack
          gap={1}
          sx={{
            display: 'flex',
            justifyContent: 'space-around',
            flexDirection: 'column',
          }}
        >
          <Controller
            name={'name'}
            control={control}
            rules={{
              required: 'Name is required.',
            }}
            render={({ field }) => (
              <TextField
                inputProps={{
                  id: 'Organizational Unit',
                }}
                InputLabelProps={{
                  htmlFor: 'Organizational Unit',
                }}
                label="Organizational Unit"
                fullWidth
                autoComplete="off"
                autoFocus
                error={!!errors.name}
                helperText={errors?.name?.message || ' '}
                required
                {...field}
              />
            )}
          />
          <Controller
            name={'description'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{
                  id: 'description',
                }}
                InputLabelProps={{
                  htmlFor: 'description',
                }}
                label="Description"
                fullWidth
                autoComplete="off"
              />
            )}
          />
          <Controller
            name="parentOrgUnitPath"
            control={control}
            render={({ field }) => (
              <Autocomplete
                id="org-unit-autocomplete"
                value={orgUnits.find((orgUnit) => {
                  if (!field.value) {
                    return orgUnit.orgUnitPath === '/';
                  } else {
                    return orgUnit.orgUnitPath === field.value;
                  }
                })}
                onChange={(_event, newValue) => {
                  setValue('parentOrgUnitPath', newValue?.orgUnitPath || '/', {
                    shouldDirty: true,
                  });
                }}
                options={orgUnits
                  .filter((org) => org.orgUnitPath !== selectedOrgUnitId)
                  .sort((a, b) => a.orgUnitPath.localeCompare(b.orgUnitPath))}
                getOptionLabel={(option) => option.orgUnitPath}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Organization Unit"
                    sx={{ minWidth: 400, mt: 3 }}
                  />
                )}
              />
            )}
          />

          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              const newOrgUnit: Partial<OrgUnitModel> = getValues();
              mutate(newOrgUnit);
            }}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        title: 'SAVE CHANGES',
        action: handleSubmit(onSubmit),
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
